import { useEffect, useState } from "react";
import { Navigate, Link, useLocation, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Bot, Check, Crown, Loader2, ShieldCheck, X } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { rememberPostLoginRedirect, clearPostLoginRedirect } from "@/lib/postLoginRedirect";

// Consent screen for the AI connector (see CLAUDE.md's "AI connector (MCP)" section).
// The backend's GET /api/oauth/authorize redirects here with ?request=<id>; approving calls
// POST /api/oauth/requests/{id}/decision, which returns the AI app's callback URL (with the
// one-time code) and we send the browser there. Not wrapped in ProtectedRoute: a signed-out
// user is sent to /login and brought back here afterwards via postLoginRedirect.js.
const CAN = [
    "See your expenses, subscriptions, sinking funds, budgets, investments and categories",
    "Add new ones — e.g. from a screenshot you share with it",
    "Edit existing ones (amounts, dates, categories, deposits, balances…)",
    "Summarise your spending",
];
const CANNOT = ["Delete anything", "Change your account, plan or household", "Edit household members' own subscriptions, funds or investments"];

export default function OAuthConsent() {
    const { user, loading } = useAuth();
    const loc = useLocation();
    const [params] = useSearchParams();
    const reqId = params.get("request") || "";
    const [info, setInfo] = useState(null);
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(null); // "approve" | "deny" | null

    useEffect(() => {
        if (!user || !reqId) return;
        clearPostLoginRedirect();
        api.get(`/oauth/requests/${encodeURIComponent(reqId)}`)
            .then(({ data }) => setInfo(data))
            .catch(e => setError(e.response?.data?.detail || "This connection request is no longer valid."));
    }, [user, reqId]);

    if (loading) return <Shell><Loader2 className="w-5 h-5 animate-spin text-slate-400 mx-auto" /></Shell>;
    if (!user) {
        rememberPostLoginRedirect(loc.pathname + loc.search);
        return <Navigate to="/login" replace />;
    }

    const decide = async (approve) => {
        setBusy(approve ? "approve" : "deny");
        try {
            const { data } = await api.post(`/oauth/requests/${encodeURIComponent(reqId)}/decision`, { approve });
            window.location.assign(data.redirect_url);
        } catch (e) {
            setError(e.response?.data?.detail || "Something went wrong. Start again from your AI app.");
            setBusy(null);
        }
    };

    if (!reqId || error) {
        return (
            <Shell>
                <h1 className="font-display text-xl font-bold text-slate-900">Couldn't connect</h1>
                <p className="text-sm text-slate-500 mt-2">{error || "Missing connection request."}</p>
                <Link to="/dashboard" className="inline-block mt-5 text-sm font-semibold text-indigo-600">Go to dashboard</Link>
            </Shell>
        );
    }
    if (!info) return <Shell><Loader2 className="w-5 h-5 animate-spin text-slate-400 mx-auto" /></Shell>;

    return (
        <Shell>
            <div className="flex items-center justify-center gap-3 mb-5">
                <span className="w-12 h-12 rounded-2xl grid place-items-center bg-slate-900 text-white"><Bot className="w-6 h-6" /></span>
                <span className="text-slate-300 text-lg">⇄</span>
                <span className="w-12 h-12 rounded-2xl grid place-items-center bg-indigo-600 text-white font-display font-extrabold text-lg">B</span>
            </div>
            <h1 className="font-display text-xl font-bold text-slate-900 text-center" data-testid="consent-title">
                Connect {info.client_name} to BudgetWise?
            </h1>
            <p className="text-sm text-slate-500 text-center mt-1">
                Signed in as <span className="font-medium text-slate-700">{info.user_email}</span>
            </p>

            {!info.plan_allowed ? (
                <div className="mt-6 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900" data-testid="consent-upsell">
                    <div className="flex items-center gap-2 font-semibold"><Crown className="w-4 h-4 text-amber-500" /> Pro feature</div>
                    <p className="mt-1 text-amber-800">Connecting AI assistants is included in the Pro and Family plans.</p>
                    <div className="flex gap-2 mt-3">
                        <Link to="/pricing" className="px-3 py-2 rounded-lg bg-amber-500 hover:bg-amber-600 text-white text-xs font-semibold">See plans</Link>
                        <Button variant="ghost" size="sm" disabled={!!busy} onClick={() => decide(false)}>Cancel</Button>
                    </div>
                </div>
            ) : (
                <>
                    <div className="mt-6 space-y-4 text-sm">
                        <div>
                            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">{info.client_name} will be able to</div>
                            <ul className="space-y-1.5">
                                {CAN.map(t => <li key={t} className="flex gap-2 text-slate-700"><Check className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />{t}</li>)}
                            </ul>
                        </div>
                        <div>
                            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">It won't be able to</div>
                            <ul className="space-y-1.5">
                                {CANNOT.map(t => <li key={t} className="flex gap-2 text-slate-500"><X className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" />{t}</li>)}
                            </ul>
                        </div>
                    </div>
                    <div className="flex gap-2 mt-7">
                        <Button variant="outline" className="flex-1 rounded-xl" disabled={!!busy} onClick={() => decide(false)} data-testid="consent-deny">
                            {busy === "deny" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Cancel"}
                        </Button>
                        <Button className="flex-1 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white" disabled={!!busy} onClick={() => decide(true)} data-testid="consent-allow">
                            {busy === "approve" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Allow"}
                        </Button>
                    </div>
                    <p className="flex items-start gap-1.5 text-[11px] text-slate-400 mt-4">
                        <ShieldCheck className="w-3.5 h-3.5 shrink-0 mt-px" />
                        You'll be sent back to {info.redirect_host}. You can disconnect any time in Settings → AI assistants.
                    </p>
                </>
            )}
        </Shell>
    );
}

function Shell({ children }) {
    return (
        <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4 py-10">
            <div className="w-full max-w-md card-elev rounded-2xl bg-white p-7">{children}</div>
        </div>
    );
}
